// 使用者與權限 › 成員群組（SPEC #org-tree-views「成員群組」、#org-tree-ui 第 2 點、o09）：
// 組織內的人員集合，只用在節點角色指派；一層、不巢狀；寫入一律 step-up；把自己加進已有指派的群組 = 自我授權（理由必填、通知）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isKinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { useConfirm } from '../../components/ConfirmDialog';
import { isOrgAdmin, type MyOrg } from '../../lib/planeApi';
import { treeApi, type MemberGroup } from '../../lib/orgTreeApi';
import { memberName } from '../people/MembersTab';
import { selfNotifyKey, useMemberGroups, useOrgMembers, useTreeRefresh } from './hooks';
import { otErr } from './otErr';

export function MemberGroupsTab({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const groups = useMemberGroups(org);
  const [edit, setEdit] = useState<MemberGroup | 'new' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refresh = useTreeRefresh(org);
  const { ask, dialog } = useConfirm();
  const admin = isOrgAdmin(org);
  async function remove(g: MemberGroup) {
    if (!(await ask({ danger: true, title: t('ot.group_delete_title', { name: g.name }), body: t('ot.group_delete_body', { n: g.assignment_count ?? 0 }), action: t('ot.group_delete') + t('ot.reauth_suffix') })))
      return;
    try {
      await treeApi.deleteMemberGroup(org, g);
      refresh();
    } catch (e) {
      setErr(otErr(t, e));
    }
  }
  return (
    <section className="card" data-testid="member-groups">
      <div className="toolbar">
        <h2 className="section-title" style={{ margin: 0 }}>
          {t('ot.member_groups')}
        </h2>
        <span className="spacer" />
        {admin && (
          <button className="btn btn-primary btn-sm" onClick={() => setEdit('new')}>
            {t('ot.group_new')}
          </button>
        )}
      </div>
      <p className="muted small">{t('ot.member_groups_hint')}</p>
      {err && <p className="error">{err}</p>}
      {groups.isLoading && <p className="muted">{t('common.loading')}</p>}
      {groups.isError && <p className="error">{otErr(t, groups.error)}</p>}
      {groups.data && groups.data.length === 0 && <p className="muted">{t('ot.no_groups')}</p>}
      {groups.data && groups.data.length > 0 && (
        <div className="table-wrap">
          <table className="grid ot-cards">
            <thead>
              <tr>
                <th>{t('ot.group_name')}</th>
                <th>{t('ot.group_members')}</th>
                <th>{t('ot.group_used')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {groups.data.map((g) => (
                <tr key={g.group_id} data-group={g.name}>
                  <td data-label={t('ot.group_name')}>▦ {g.name}</td>
                  <td data-label={t('ot.group_members')}>{t('ot.n_people', { n: g.member_count })}</td>
                  <td data-label={t('ot.group_used')}>{t('ot.n_assignments', { n: g.assignment_count ?? 0 })}</td>
                  <td>
                    {admin && (
                      <div className="row">
                        <button className="btn btn-sm" onClick={() => setEdit(g)}>
                          {t('common.edit')}
                        </button>
                        <button className="btn btn-sm btn-danger-outline" onClick={() => void remove(g)}>
                          {t('common.action_delete')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <GroupDialog org={org} group={edit === 'new' ? null : edit} close={() => setEdit(null)} done={refresh} />}
      {dialog}
    </section>
  );
}

function GroupDialog({ org, group, close, done }: { org: MyOrg; group: MemberGroup | null; close(): void; done(): void }) {
  const { t } = useTranslation();
  const members = useOrgMembers(org);
  const [full, setFull] = useState<MemberGroup | null>(null);
  const [loaded, setLoaded] = useState(!group);
  const [name, setName] = useState(group?.name ?? '');
  const [ids, setIds] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [needReason, setNeedReason] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (group && !loaded) {
    setLoaded(true);
    void treeApi
      .memberGroup(org, group.group_id)
      .then((g) => {
        setFull(g);
        setIds(new Set((g.members ?? []).map((m) => m.user_id)));
      })
      .catch((e) => setErr(otErr(t, e)));
  }
  const me = (members.data ?? []).find((m) => m.is_me);
  const wasIn = !!me && !!full?.members?.some((m) => m.user_id === me.user_id);
  const selfAdd = !!me && ids.has(me.user_id) && !wasIn && (group?.assignment_count ?? 0) > 0;
  async function save() {
    if ((selfAdd || needReason) && !reason.trim()) {
      setNeedReason(true);
      return setErr(t('org.err_REASON_REQUIRED'));
    }
    setBusy(true);
    setErr(null);
    try {
      const body = { name: name.trim(), member_ids: [...ids], ...(reason.trim() ? { reason: reason.trim() } : {}) };
      if (group) await treeApi.updateMemberGroup(org, full ?? group, body);
      else await treeApi.createMemberGroup(org, body);
      done();
      close();
    } catch (e) {
      if (isKinioError(e) && e.code === 'REASON_REQUIRED') {
        setNeedReason(true);
        setErr(t('ot.reason_needed_self'));
      } else setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={group ? t('ot.group_edit_title', { name: group.name }) : t('ot.group_new')} onClose={close}>
      <div className="form">
        <label className="field">
          <span>{t('ot.group_name')}</span>
          <input className="input" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <fieldset className="field">
          <legend>{t('ot.group_members')}</legend>
          <div className="ot-pick-list">
            {(members.data ?? [])
              .filter((m) => m.status !== 'suspended')
              .map((m) => (
                <label key={m.user_id} className="ot-pick">
                  <input
                    type="checkbox"
                    checked={ids.has(m.user_id)}
                    onChange={(e) => {
                      const n = new Set(ids);
                      if (e.target.checked) n.add(m.user_id);
                      else n.delete(m.user_id);
                      setIds(n);
                    }}
                  />
                  {memberName(m)}
                  {m.is_me && <span className="badge">{t('ot.you')}</span>}
                </label>
              ))}
          </div>
        </fieldset>
        {(selfAdd || needReason) && (
          <>
            <p className="ot-warn small">
              {t('ot.self_group_yellow', { n: group?.assignment_count ?? 0 })} {t('ot.self_notify_' + selfNotifyKey(org, members.data))}
            </p>
            <label className="field">
              <span>{t('ot.reason')} *</span>
              <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} aria-label={t('ot.reason')} />
            </label>
          </>
        )}
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim() || name.trim().length > 40} onClick={() => void save()}>
            {t('common.save')}
            {t('ot.reauth_suffix')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
